// Security Utilities - Industry standard input validation and sanitization
const SecurityUtils = {
  // Sanitize text input to prevent XSS attacks
  sanitizeText: (input) => {
    if (!input || typeof input !== 'string') return '';

    // Remove any script tags and their content
    let sanitized = input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

    // Remove event handlers (onclick, onerror, etc.)
    sanitized = sanitized.replace(/on\w+\s*=\s*["'][^"']*["']/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=\s*[^\s>]*/gi, '');

    // Remove javascript: protocol
    sanitized = sanitized.replace(/javascript:/gi, '');

    // Remove data: protocol (can be used for XSS)
    sanitized = sanitized.replace(/data:text\/html/gi, '');

    // Remove HTML tags (React handles safe text rendering)
    sanitized = sanitized.replace(/<[^>]*>/g, '');

    // Limit length to prevent DOS attacks
    return sanitized.substring(0, 10000);
  },

  // Validate file uploads
  validateFile: (file, options = {}) => {
    const defaults = {
      maxSize: 10 * 1024 * 1024, // 10MB default
      allowedImageTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
      allowedVideoTypes: ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'],
      allowedExtensions: {
        image: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
        video: ['.mp4', '.webm', '.ogg', '.mov']
      }
    };

    const config = { ...defaults, ...options };

    if (!file) {
      return { valid: false, error: 'No file provided' };
    }

    // Check file size
    if (file.size > config.maxSize) {
      return {
        valid: false,
        error: `File size exceeds maximum allowed size of ${config.maxSize / (1024 * 1024)}MB`
      };
    }

    // Check file type based on MIME type
    const isImage = config.allowedImageTypes.includes(file.type);
    const isVideo = config.allowedVideoTypes.includes(file.type);

    if (!isImage && !isVideo) {
      return {
        valid: false,
        error: 'Invalid file type. Only images (JPEG, PNG, GIF, WebP) and videos (MP4, WebM, OGG, MOV) are allowed.'
      };
    }

    // Check file extension as additional validation
    const fileName = file.name.toLowerCase();
    const validExtension =
      config.allowedExtensions.image.some(ext => fileName.endsWith(ext)) ||
      config.allowedExtensions.video.some(ext => fileName.endsWith(ext));

    if (!validExtension) {
      return {
        valid: false,
        error: 'Invalid file extension'
      };
    }

    // Check for double extensions (e.g., file.jpg.exe)
    const parts = fileName.split('.');
    if (parts.length > 2) {
      return {
        valid: false,
        error: 'Invalid file name format'
      };
    }

    // Validate filename doesn't contain malicious patterns
    if (/[<>:"\/\\|?*\x00-\x1f]/g.test(fileName)) {
      return {
        valid: false,
        error: 'Invalid characters in filename'
      };
    }

    return {
      valid: true,
      type: isImage ? 'image' : 'video',
      sanitizedName: fileName.replace(/[^a-zA-Z0-9._-]/g, '_')
    };
  }
};

export default SecurityUtils;
